import { type LogEventModel, useEventLogs } from "@/api/logs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUsers } from "@/features/users/hooks/useUsers";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { formatTime } from "@/firestore_types";
import { createFileRoute } from "@tanstack/react-router";
import { Clock, RefreshCw, User } from "lucide-react";

export const Route = createFileRoute("/dashboard/admin/event_logs/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, refetch } = useEventLogs(20);
  const { workers } = useAllWorkers();
  const { users } = useUsers();

  const logs = data?.pages.flatMap((page) => page.logs) ?? [];

  const getUserName = (log: LogEventModel) => {
    if (
      log.type === "worker_clocked_in" ||
      log.type === "worker_clocked_out" ||
      log.type === "worker_lunch_started" ||
      log.type === "worker_lunch_ended"
    ) {
      if (log.workerId) {
        const worker = workers.find((w) => w.id === log.workerId);
        if (worker) {
          return worker.name;
        }
      }
    }

    if (log.actorId === "unknown") return "System";
    const user = users.find((u) => u.uid === log.actorId);
    return user?.name || user?.email || log.actorId;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-1 sm:p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Event Logs</h1>
            <p className="text-muted-foreground">System activity and audit trail</p>
          </div>
        </div>
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-1 sm:p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Event Logs</h1>
          <p className="text-muted-foreground">System activity and audit trail</p>
        </div>
        <Button
          onClick={() => refetch()}
          disabled={isFetching}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {logs.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-muted-foreground">No event logs found</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="overflow-hidden rounded-md border">
            <Table className="w-full">
              <TableHeader className="bg-muted/50 border-b">
                <TableRow>
                  <TableHead className="p-3 text-left font-medium">User</TableHead>
                  <TableHead className="p-3 text-left font-medium">Description</TableHead>
                  <TableHead className="p-3 text-left font-medium">Time</TableHead>
                  <TableHead className="p-3 text-left font-medium">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, index) => (
                  <TableRow
                    key={`${log.timestamp}-${index}`}
                    className="hover:bg-muted/50 border-b"
                  >
                    <TableCell className="p-3">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span>{getUserName(log)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="p-3">
                      <span className="font-medium">{log.description}</span>
                    </TableCell>
                    <TableCell className="p-3">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatTime(log.timestamp, true)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="p-3">
                      {log.payload && Object.keys(log.payload).length > 0 ? (
                        <div className="text-muted-foreground text-sm">
                          {Object.entries(log.payload).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium">{key}:</span> {String(value)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetching}
                variant="outline"
                className="flex items-center gap-2"
              >
                {isFetching ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
